import React, { useCallback, useEffect, useReducer, useRef } from 'react';

import type { HTMLProps } from 'react';

import { twMerge } from 'tailwind-merge';

import Typography from 'components/typography/typography';

import Icon from 'components/icon/icon';

import type { SupportedFilesTypes } from './supportedfileTypes';

interface FileInputProps extends HTMLProps<HTMLInputElement> {
  label: string;
  fileNameAreaClassName?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  supportedFileTypes?: SupportedFilesTypes[];
  resetAfterSubmit?: boolean;
  onResetFile?: () => void;
}

const FileInput: React.FC<FileInputProps> = (props) => {
  const {
    label,
    onChange,
    fileNameAreaClassName,
    wrapperClassName,
    buttonClassName,
    supportedFileTypes,
    onResetFile,
    multiple,
    resetAfterSubmit,
    ...inputFieldProps
  } = props;

  const [fileNames, setFileNames] = useReducer(
    (prevFileName: string[], newFileName: string[]) => {
      return newFileName;
    },
    [],
  );

  const fileInputRef: React.LegacyRef<HTMLInputElement> = useRef(null);

  const supportedTypesAttribute = supportedFileTypes?.join(', ');

  const resetFileInput = useCallback(() => {
    onResetFile?.();
    setFileNames([]);
    if (fileInputRef.current) {
      fileInputRef.current.files = null;
      fileInputRef.current.value = null as unknown as string;
    }
  }, [setFileNames, onResetFile]);

  const checkFileTypes = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): boolean => {
      let success = true;
      if (!event.target.files) return false;
      Array.from(event.target.files).forEach((file) => {
        if (
          (supportedTypesAttribute &&
            !supportedTypesAttribute.toLocaleLowerCase().includes(file.type)) ||
          file.type === ''
        )
          success = false;
      });

      return success;
    },
    [supportedTypesAttribute],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (
        (!supportedTypesAttribute && event.target.files) ||
        (event.target.files && supportedTypesAttribute && checkFileTypes(event))
      ) {
        const fileNamesList: string[] = [];
        Array.from(event.target.files).forEach((file: File) => {
          fileNamesList.push(file.name);
        });
        setFileNames(fileNamesList);
        onChange?.(event);
      } else {
        setFileNames([]);
        if (fileInputRef?.current) {
          fileInputRef.current.files = null;
          fileInputRef.current.value = null as unknown as string;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportedTypesAttribute, onChange, setFileNames],
  );

  useEffect(() => {
    if (resetAfterSubmit && fileInputRef.current) {
      fileInputRef.current.files = null;
      fileInputRef.current.value = null as unknown as string;
      setFileNames([]);
    }
  });

  const fileNameClassName = twMerge(
    'flex justify-start items-center w-[85%]',
    'bg-gray100 rounded-sm border',
    'border-gray100 mt-6 px-2 py-2',
    fileNameAreaClassName,
  );

  const containerClassName = twMerge(
    'flex flex-col justify-center items-start w-full h-fit',
    wrapperClassName,
  );

  const fileButtonClassname = twMerge(
    'relative flex justify-center items-center',
    'py-2 px-8 bg-white border border-gray100 rounded-lg mt-6 w-full',
    buttonClassName,
  );

  return (
    <div className={containerClassName}>
      {fileNames &&
        fileNames.map((fileName) => {
          return (
            <div className={fileNameClassName} key={fileName}>
              <Icon icon="attachment" size={18} className="text-gray800" />
              <Typography
                as="h2"
                variant="body-medium-bold"
                className="text-gray800 whitespace-nowrap ml-2 overflow-x-scroll w-[85%]"
              >
                {fileName}
              </Typography>
              {!multiple && (
                <div className="flex justify-end items-end cursor-pointer">
                  <Icon icon="close" size={18} onClick={resetFileInput} />
                </div>
              )}
            </div>
          );
        })}

      <div className={fileButtonClassname}>
        {multiple && !!fileNames.length && (
          <Icon
            icon="close"
            size={18}
            onClick={resetFileInput}
            className="absolute top-3 right-2 z-20 cursor-pointer"
          />
        )}

        <Icon icon="upload" size={18} className="mr-3 text-gray600" />

        <Typography as="h3" variant="body-large-bold" className="text-gray600 ">
          {label}
        </Typography>
        <input
          multiple={multiple}
          ref={fileInputRef}
          type="file"
          accept={supportedTypesAttribute}
          className="w-full h-full absolute opacity-0 file:w-[100%] file:h-full z-10 file:cursor-pointer"
          onChange={handleFileChange}
          {...inputFieldProps}
        />
      </div>
    </div>
  );
};

export default FileInput;
